import { stringify } from 'query-string';
import { useCallback, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router';
import * as z from 'zod';
import { parseQueryStringPreprocessor } from '../../lib/parseQueryStringPreprocessor';
import { StorageKey } from '../../redux/modules/localStorage/actions';

type UseStoredQuery<T, U extends z.ZodTypeDef, V> = {
  parser: z.ZodType<T, U, V>;
  localStorageKey: StorageKey;
};

export const useStoredQuery = <T, U extends z.ZodTypeDef, V>({
  parser,
  localStorageKey,
}: UseStoredQuery<T, U, V>) => {
  const { replace } = useHistory();
  const { search } = useLocation();

  const preprocessedParser = useMemo(
    () => z.preprocess(parseQueryStringPreprocessor, parser),
    [parser]
  );

  // Parse empty string to get potential default values from parser
  const defaultQuery = useMemo(() => {
    return preprocessedParser.parse('');
  }, [preprocessedParser]);

  // Parsed URL query and return it or undefined if it's not valid
  const parsedParams = useMemo(() => {
    const parsedQuery = preprocessedParser.safeParse(search);
    return parsedQuery.success ? parsedQuery.data : undefined;
  }, [preprocessedParser, search]);

  // Parsed LS query
  // If query from LS is valid and query from URL is empty of invalid => update URL
  useEffect(() => {
    const storedQueryString = localStorage.getItem(localStorageKey);
    const storedQuery = preprocessedParser.safeParse(storedQueryString);
    const isUrlEmptyOrInvalid = !parsedParams || search === '';

    if (storedQueryString && storedQuery.success && isUrlEmptyOrInvalid) {
      replace({ search: storedQueryString });
    }
  }, [localStorageKey, parsedParams, preprocessedParser, replace, search]);

  // Update new query
  const updateParsedParams = useCallback(
    (state: Partial<typeof parsedParams>) => {
      const locationQuery = state
        ? stringify({ ...parsedParams, ...state }, { arrayFormat: 'comma' })
        : '';

      localStorage.setItem(localStorageKey, locationQuery);
      replace({ search: locationQuery });
    },
    [localStorageKey, parsedParams, replace]
  );

  return [parsedParams ?? defaultQuery, updateParsedParams] as const;
};
